import { useQuery, useQueryClient, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import API from './utils/api';
import { 
    CreateEmployeeWorkTimeRequestDto,
    UpdateEmployeeWorkTimeRequestDto,
    QueryEmployeeWorkTimeRequestDto,
    EmployeeWorkTimeRequestPaginator,
    EmployeeWorkTimeRequestDto,
    EmployeeWorkTimeInputDto
} from '../models/employee-work-time-request.dto';
import { RevokeTimeRequestDto } from '../models/time-request.dto';
import ENDPOINTS from './utils/endpoints';

const { GET, DETAIL } = ENDPOINTS.HR.EMPLOYEE_WORK_TIME_REQUEST;

// Lists and the open request both change when a request does
function useInvalidateRequests() {
    const queryClient = useQueryClient();
    return () => {
        queryClient.invalidateQueries(GET);
        queryClient.invalidateQueries(DETAIL);
    };
}

export function useGetEmployeeWorkTimeRequest(
    options?: QueryEmployeeWorkTimeRequestDto,
    queryOptions?: { staleTime?: number, refetchOnWindowFocus?: boolean },
) {
    const request = (options: QueryEmployeeWorkTimeRequestDto) => API.HrAPI.getEmpWorkTimeRequests(options);

    const { data, isLoading, error, refetch } = useQuery<EmployeeWorkTimeRequestPaginator, Error>(
        [GET, options],
        ({ queryKey, pageParam }) => request(Object.assign({}, queryKey[1], pageParam)),
        { ...API.Handlers.pageParamOption, ...queryOptions },
    );

    return { data, isLoading, error, refetch };
}

export function useGetEmployeeWorkTimeRequestById(id?: number) {
    const { data, isLoading, error } = useQuery<{ data: EmployeeWorkTimeRequestDto }, Error>(
        [DETAIL, id],
        () => API.HrAPI.getEmpWorkTimeRequestById(id!),
        { enabled: !!id },
    );
    
    return { data, isLoading, error };
}

export function useCreateEmployeeWorkTimeRequest(onSuccessCallback?: (responsData?: any) => void) {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ payload }: { payload: CreateEmployeeWorkTimeRequestDto }) => API.HrAPI.createEmpWorkTimeRequest(payload),
        {
            onSuccess: (response) => {
                toast.success('Work time request submitted for approval');
                if (onSuccessCallback) {
                    onSuccessCallback(response);
                };
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: invalidate,
        },
    );
}

export function useUpdateEmployeeWorkTimeRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id, payload }: { id: number, payload: UpdateEmployeeWorkTimeRequestDto }) => API.HrAPI.updateEmpWorkTimeRequest(id, payload),
        {
            onSuccess: () => {
                toast.success('Work time request updated successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: invalidate,
        },
    );
}

export function useDeleteEmployeeWorkTimeRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id }: { id: number }) => API.HrAPI.deleteEmpWorkTimeRequest(id),
        {
            onSuccess: () => {
                toast.success('Work time request deleted successfully');
            },
            onError: API.Handlers.onErrorHandler,
            onSettled: invalidate,
        },
    );
}

export function useAddEmployeeWorkTimeResponse() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id, payload }: { id: number, payload: EmployeeWorkTimeInputDto }) => API.HrAPI.addEmpWorkTimeResponse(id, payload),
        {
            onSuccess: ({ data }) => {
                toast.success('Response submitted successfully');
                if (data.payrollRerunRequired) {
                    toast.warning(
                        `Payroll for ${data.payPeriod?.code ?? 'this pay period'} was already run; `
                        + 'run it again to include this work time'
                    );
                }
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        },
    );
}

export function useCancelEmployeeWorkTimeRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id }: { id: number }) => API.HrAPI.cancelEmpWorkTimeRequest(id),
        {
            onSuccess: () => {
                toast.success('Work time request cancelled successfully');
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        },
    );
}

export function useRevokeEmployeeWorkTimeRequest() {
    const invalidate = useInvalidateRequests();

    return useMutation(
        ({ id, payload }: { id: number, payload: RevokeTimeRequestDto }) => API.HrAPI.revokeEmpWorkTimeRequest(id, payload),
        {
            onSuccess: ({ data }) => {
                toast.success('Work time request revoked successfully');
                if (data.payrollRerunRequired) {
                    toast.warning('Payroll had already been run with this work time; run it again to remove it');
                }
                invalidate();
            },
            onError: API.Handlers.onErrorHandler,
        },
    );
}
